import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Button, IconButton,
  Paper, Chip, CircularProgress, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, FormControl, InputLabel, Tooltip, CardActions,
  Stack, Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Clear as ClearIcon,
  Translate as TranslateIcon,
  MenuBook as StoriesIcon,
} from '@mui/icons-material';
import apiClient from '../../services/apiClient';

// Proficiency level options
const proficiencyLevels = [
  { value: 'A0', label: 'A0 (Absolute Beginner)' },
  { value: 'A1', label: 'A1 (Beginner)' },
  { value: 'A2', label: 'A2 (Elementary)' },
  { value: 'B1', label: 'B1 (Intermediate)' },
  { value: 'B2', label: 'B2 (Upper Intermediate)' },
  { value: 'C1', label: 'C1 (Advanced)' },
  { value: 'C2', label: 'C2 (Proficient)' }
];

const PathList = () => {
  const navigate = useNavigate();

  // State
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // published, draft, all
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pathToDelete, setPathToDelete] = useState(null);

  // Load paths on component mount
  useEffect(() => {
    fetchPaths();
  }, []);

  // Fetch paths from API
  const fetchPaths = async () => {
    setLoading(true);
    try {
      const response = await apiClient.admin.getPaths();
      setPaths(response.data.paths || []);
    } catch (error) {
      console.error('Error fetching paths:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // Filter paths based on search term and filters
  const filteredPaths = paths.filter(path => {
    const matchesSearch = path.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        path.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesLevel = levelFilter ? path.level === levelFilter : true;

    const matchesStatus = statusFilter ?
      (statusFilter === 'published' ? path.is_published : !path.is_published) :
      true;

    return matchesSearch && matchesLevel && matchesStatus;
  });

  // Handle filters
  const handleLevelFilterChange = (event) => {
    setLevelFilter(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const clearFilters = () => {
    setLevelFilter('');
    setStatusFilter('');
    setSearchTerm('');
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Handle delete
  const openDeleteDialog = (path) => {
    setPathToDelete(path);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setPathToDelete(null);
  };

  const confirmDelete = async () => {
    if (!pathToDelete) return;

    try {
      await apiClient.admin.deletePath(pathToDelete.path_id);
      setPaths(prevPaths => prevPaths.filter(path => path.path_id !== pathToDelete.path_id));
      closeDeleteDialog();
    } catch (error) {
      console.error('Error deleting path:', error);
    }
  };

  const hasFilters = Boolean(searchTerm || levelFilter || statusFilter);

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 3, gap: 1 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Learning Paths
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {loading
              ? 'Loading…'
              : `${filteredPaths.length} of ${paths.length} path${paths.length === 1 ? '' : 's'}`}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/paths/new')}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          New Path
        </Button>
      </Stack>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search paths"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={clearSearch}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="level-filter-label">Level</InputLabel>
              <Select
                labelId="level-filter-label"
                value={levelFilter}
                label="Level"
                onChange={handleLevelFilterChange}
              >
                <MenuItem value="">All Levels</MenuItem>
                {proficiencyLevels.map(level => (
                  <MenuItem key={level.value} value={level.value}>
                    {level.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              disabled={!hasFilters}
              fullWidth
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Paths Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filteredPaths.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No paths found.
          </Typography>
          {hasFilters ? (
            <Button
              variant="outlined"
              size="small"
              onClick={clearFilters}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Clear all filters
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/admin/paths/new')}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Create your first path
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={2.5}>
          {filteredPaths.map((path) => (
            <Grid item xs={12} md={6} lg={4} key={path.path_id}>
              <Card
                variant="outlined"
                onClick={() => navigate(`/admin/paths/${path.path_id}`)}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  cursor: 'pointer',
                  transition: 'box-shadow .15s, border-color .15s, transform .15s',
                  '&:hover': {
                    boxShadow: 3,
                    borderColor: 'primary.main',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Path Header */}
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 1.5 }}>
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                      {path.title}
                    </Typography>
                    {path.is_published ? (
                      <Chip label="Published" color="success" size="small" variant="outlined" />
                    ) : (
                      <Chip label="Draft" size="small" variant="outlined" />
                    )}
                  </Stack>

                  <Box sx={{ mb: 1.5 }}>
                    <Chip label={path.level} size="small" variant="outlined" color="primary" />
                  </Box>

                  {/* Description */}
                  {path.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {path.description.length > 100
                        ? `${path.description.substring(0, 100)}...`
                        : path.description}
                    </Typography>
                  )}

                  {/* Stats */}
                  <Stack direction="row" spacing={2.5} sx={{ mb: 1.5 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <StoriesIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {path.stories_count || 0} stories
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <TranslateIcon fontSize="small" color={path.translation_count > 0 ? 'success' : 'action'} />
                      <Typography variant="body2" color="text.secondary">
                        {path.translation_count || 0} translations
                      </Typography>
                    </Stack>
                  </Stack>

                  {/* Tags */}
                  {path.tags && path.tags.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                      {path.tags.slice(0, 3).map(tag => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                      ))}
                      {path.tags.length > 3 && (
                        <Chip label={`+${path.tags.length - 3}`} size="small" variant="outlined" />
                      )}
                    </Box>
                  )}

                  {/* Updated Date */}
                  <Typography variant="caption" color="text.secondary">
                    Updated {formatDate(path.updated_at)}
                  </Typography>
                </CardContent>

                <Divider sx={{ borderColor: 'divider' }} />

                {/* Actions */}
                <CardActions
                  sx={{ justifyContent: 'space-between', px: 2, py: 1 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Box>
                    <Tooltip title="View">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/admin/paths/${path.path_id}`)}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/admin/paths/${path.path_id}/edit`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => openDeleteDialog(path)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(`/admin/paths/${path.path_id}`)}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    Manage
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Path</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the path "{pathToDelete?.title}"?
          </Typography>
          <Typography color="error" sx={{ mt: 2 }}>
            This will also delete all stories within this path and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeleteDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained" sx={{ textTransform: 'none', borderRadius: 2 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PathList;
